// Browser push over Firebase Cloud Messaging.
//
// The Firebase config comes from the server (`GET /push/config`), so turning
// push on needs no rebuild. Firebase is imported lazily: it's ~80 kB that only
// people who enable push should download. Our own service worker
// (`public/push-sw.js`) shows the notifications; the server sends data-only
// messages, so nothing is shown twice.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDevices, usePushConfig, useRegisterDevice, useUnregisterDevice } from "@/api/notify";
import type { PushConfig } from "@/api/types/PushConfig";

const SW_PATH = "/push-sw.js";
const TOKEN_KEY = "mixla.pushToken";

type ReadyConfig = { firebase: Record<string, string>; vapid_key: string };

function ready(c: PushConfig | undefined): c is ReadyConfig {
  return !!c?.firebase && !!c.vapid_key;
}

async function firebaseApp(c: ReadyConfig) {
  const { initializeApp, getApps, getApp } = await import("firebase/app");
  return getApps().length > 0 ? getApp() : initializeApp(c.firebase);
}

/** False on browsers without service workers / Push (e.g. iOS Safari outside an installed web app). */
async function supported(): Promise<boolean> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("Notification" in window)) return false;
  try {
    const { isSupported } = await import("firebase/messaging");
    return await isSupported();
  } catch {
    return false;
  }
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = await navigator.serviceWorker.register(SW_PATH);
  await navigator.serviceWorker.ready;
  return reg;
}

/** Always pass our registration: without it the SDK registers its own worker, which shows nothing. */
async function getToken(c: ReadyConfig): Promise<string | null> {
  const app = await firebaseApp(c);
  const { getMessaging, getToken } = await import("firebase/messaging");
  const token = await getToken(getMessaging(app), { vapidKey: c.vapid_key, serviceWorkerRegistration: await registration() });
  return token || null;
}

async function deleteToken(c: ReadyConfig) {
  try {
    const app = await firebaseApp(c);
    const { getMessaging, deleteToken } = await import("firebase/messaging");
    await deleteToken(getMessaging(app));
  } catch {
    // Best effort: the server also drops tokens FCM reports as dead.
  }
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage blocked: push still works this session.
  }
}

/**
 * - `unsupported`: push isn't set up on the server, or this browser can't do it.
 * - `blocked`: notifications are denied in the browser's site settings.
 * - `on` / `off`: this browser's token is / isn't registered with Mixla.
 */
export type PushState = "loading" | "unsupported" | "blocked" | "on" | "off";

/**
 * The push switch. "On" means this browser's token is in `GET /devices` (the
 * server deletes dead tokens, so the device list is the truth), not just that
 * the browser allows notifications.
 */
export function usePush() {
  const config = usePushConfig();
  const cfg = ready(config.data) ? config.data : null;
  const [canPush, setCanPush] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [token, setToken] = useState<string | null>(readToken);
  const [justRegistered, setJustRegistered] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const devices = useDevices(canPush === true);
  const register = useRegisterDevice();
  const unregister = useUnregisterDevice();

  useEffect(() => {
    if (!cfg) return;
    let cancelled = false;
    void supported().then((ok) => {
      if (cancelled) return;
      setCanPush(ok);
      if (ok) setPermission(Notification.permission);
    });
    return () => {
      cancelled = true;
    };
  }, [cfg]);

  const registered = !!token && (justRegistered === token || (devices.data?.some((d) => d.fcm_token === token) ?? false));
  let state: PushState;
  if (config.isPending) state = "loading";
  else if (!cfg || canPush === false) state = "unsupported";
  else if (canPush === null || devices.isPending) state = "loading";
  else if (permission === "denied") state = "blocked";
  else state = registered ? "on" : "off";

  // Tokens can rotate and the JS SDK has no refresh event: re-check once per visit.
  const rotationChecked = useRef(false);
  useEffect(() => {
    if (state !== "on" || !cfg || rotationChecked.current) return;
    rotationChecked.current = true;
    void (async () => {
      try {
        const fresh = await getToken(cfg);
        if (!fresh || fresh === token) return;
        await register.mutateAsync({ fcm_token: fresh, platform: "web" });
        writeToken(fresh);
        setJustRegistered(fresh);
        setToken(fresh);
      } catch {
        // The old token keeps working until FCM retires it.
      }
    })();
  }, [state, cfg, token, register]);

  const enable = useCallback(async () => {
    if (!cfg) return;
    setError(null);
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return;
      const fresh = await getToken(cfg);
      if (!fresh) throw new Error("no push token");
      await register.mutateAsync({ fcm_token: fresh, platform: "web" });
      writeToken(fresh);
      setJustRegistered(fresh);
      setToken(fresh);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [cfg, register]);

  const disable = useCallback(async () => {
    if (!cfg) return;
    setError(null);
    setBusy(true);
    try {
      const current = token ?? (await getToken(cfg));
      if (current) await unregister.mutateAsync(current);
      await deleteToken(cfg);
      writeToken(null);
      setJustRegistered(null);
      setToken(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [cfg, token, unregister]);

  return { state, enable, disable, busy, error };
}

/** Clicking a push focuses an open Mixla tab; the service worker asks it to route there. */
export function usePushNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; link?: string } | null;
      if (d?.type === "mixla-open" && d.link?.startsWith("/")) void navigate(d.link);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
}
